import { ReactNode, useEffect, useState } from "react";

import { ActionIcon, Affix, Menu, Stack, Tooltip } from "@mantine/core";

import {
  IconAmbulance,
  IconBellRinging,
  IconDoorEnter,
  IconDroplet,
  IconSos,
} from "@tabler/icons-react";

import { useTimerContext } from "@/components/Timer/TimerContext";

import {
  LABOR_QUESTIONNAIRE_DISPLAY_NAMES,
  QuestionnaireType,
} from "../../constants/labor-questionnaires";
import { EncounterLaborQuickButtonDrawer } from "../../EncounterLaborDrawer/EncounterLaborQuickButtonDrawer";

import classes from "./EncounterLaborHotKeys.module.css";
import { EncounterLaborQuickButtonsProps } from "./EncounterLaborQuickButtons";
import SOSMenuItems from "./SOSMenuItems";

/** The reminder hotkey's name and touch label follow whether a reminder is waiting. */
const REMINDER_LABELS = {
  active: { name: "Acknowledge Reminders", shortLabel: "Acknowledge" },
  idle: { name: "No Reminders Active", shortLabel: "No reminders" },
} as const;

const HOT_KEY_BUTTONS = [
  {
    icon: <IconDoorEnter size={24} color="var(--mantine-color-dark-7)" />,
    questionnaireName: QuestionnaireType.AdmitDischarge,
    displayTitle: LABOR_QUESTIONNAIRE_DISPLAY_NAMES[QuestionnaireType.AdmitDischarge],
    shortLabel: "Admit",
  },
  {
    icon: <IconSos size={24} color="var(--mantine-color-dark-7)" />,
    displayTitle: "SOS",
    shortLabel: "SOS",
  },
  {
    icon: <IconAmbulance size={24} color="var(--mantine-color-dark-7)" />,
    questionnaireName: QuestionnaireType.Transport,
    displayTitle: LABOR_QUESTIONNAIRE_DISPLAY_NAMES[QuestionnaireType.Transport],
    shortLabel: "Transport",
  },
  {
    icon: <IconDroplet size={24} color="var(--mantine-color-dark-7)" />,
    questionnaireName: QuestionnaireType.EstimatedBloodLoss,
    displayTitle: LABOR_QUESTIONNAIRE_DISPLAY_NAMES[QuestionnaireType.EstimatedBloodLoss],
    shortLabel: "EBL",
  },
  {
    icon: <IconBellRinging size={24} color="var(--mantine-color-dark-7)" />,
    displayTitle: "Reminder",
    shortLabel: REMINDER_LABELS.idle.shortLabel,
  },
] as const;

type HotKey = (typeof HOT_KEY_BUTTONS)[number];

/**
 * One hotkey on the rail. Tooltips never open on a touch screen, so there the short label shows
 * beside the icon; screen readers skip it and read the button's own name.
 */
function HotKeyRow({ shortLabel, children }: { shortLabel: string; children: ReactNode }) {
  return (
    <div data-testid="hot-key-row" className={classes.hotKeyRow}>
      <span aria-hidden="true" className={classes.shortLabel}>
        {shortLabel}
      </span>
      {children}
    </div>
  );
}

const HOT_KEY_ICONS = {
  SOS: ({
    hotKey,
    selectedHotKey,
    onClick,
    encounterId,
    clientId,
    onRefresh,
    setSelectedHotKey,
  }: {
    hotKey: HotKey;
    selectedHotKey: HotKey | null;
    onClick: () => void;
    encounterId: string;
    clientId: string;
    onRefresh?: () => Promise<void>;
    setSelectedHotKey: (hotKey: HotKey | null) => void;
  }) => (
    <Menu
      position="left"
      onClose={() => {
        onClick();
        setSelectedHotKey(null);
      }}
    >
      {/* Outside Menu.Target, which passes the menu's toggle and ref through to the button. */}
      <Tooltip label="Emergency Options">
        <Menu.Target>
          <ActionIcon
            size={50}
            radius="xl"
            autoContrast
            variant="filled"
            onClick={onClick}
            aria-label="Emergency Options"
            data-testid="sos-menu-button-wrapper"
            color={selectedHotKey?.displayTitle === hotKey.displayTitle ? "gray.5" : "gray.1"}
          >
            <IconSos size={24} color="var(--mantine-color-dark-7)" />
          </ActionIcon>
        </Menu.Target>
      </Tooltip>
      <SOSMenuItems clientId={clientId} onRefresh={onRefresh} encounterId={encounterId} />
    </Menu>
  ),
  Reminder: ({
    hotKey,
    isReminderActive,
    onClick,
  }: {
    hotKey: HotKey;
    isReminderActive: boolean;
    onClick: () => void;
  }) => {
    const { name } = REMINDER_LABELS[isReminderActive ? "active" : "idle"];
    return (
      <Tooltip label={name}>
        <ActionIcon
          size={50}
          radius="xl"
          autoContrast
          variant="filled"
          aria-label={name}
          onClick={onClick}
          data-testid="hot-key-icon-reminder"
          color={isReminderActive ? "red.7" : "gray.1"}
        >
          <IconBellRinging
            size={24}
            color={isReminderActive ? "white" : "var(--mantine-color-dark-7)"}
          />
        </ActionIcon>
      </Tooltip>
    );
  },
  Default: ({
    hotKey,
    selectedHotKey,
    onClick,
    setSelectedHotKey,
  }: {
    hotKey: HotKey;
    selectedHotKey: HotKey | null;
    onClick: () => void;
    setSelectedHotKey: (hotKey: HotKey | null) => void;
  }) => (
    <Tooltip label={hotKey.displayTitle}>
      <ActionIcon
        size={50}
        radius="xl"
        autoContrast
        variant="filled"
        onClick={onClick}
        aria-label={hotKey.displayTitle}
        data-testid={`hot-key-icon-${hotKey.displayTitle}`}
        color={selectedHotKey?.displayTitle === hotKey.displayTitle ? "gray.5" : "gray.1"}
      >
        {hotKey.icon}
      </ActionIcon>
    </Tooltip>
  ),
} as const;

interface EncounterLaborHotKeysProps extends EncounterLaborQuickButtonsProps {
  onRefresh?: () => Promise<void>;
}

export default function EncounterLaborHotKeys({
  encounterId,
  clientId,
  onRefresh,
}: EncounterLaborHotKeysProps) {
  const [selectedHotKey, setSelectedHotKey] = useState<HotKey | null>(null);
  const [opened, setOpened] = useState(false);
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState<{
    questionnaireName: QuestionnaireType;
    displayTitle: string;
  } | null>(null);
  const { hasActiveAlarms, dismissAllAlarms } = useTimerContext();

  // Check for active alarms periodically and keep the reminder active
  // until the user explicitly acknowledges/dismisses them.
  const [isReminderActive, setIsReminderActive] = useState(false);
  useEffect(() => {
    const checkAlarms = () => {
      if (hasActiveAlarms()) {
        setIsReminderActive(true);
      }
    };

    // Check immediately
    checkAlarms();

    // Check every second for active alarms
    const interval = setInterval(checkAlarms, 1000);

    return () => clearInterval(interval);
  }, [hasActiveAlarms]);

  const handleHotKeyChange = (hotKey: HotKey) => {
    if (hotKey.displayTitle === "Reminder") {
      // Dismiss all alarms (reset and stop timers with active alarms)
      if (isReminderActive) {
        dismissAllAlarms();
        setIsReminderActive(false);
      }
      return;
    }

    setSelectedHotKey(hotKey);

    if ("questionnaireName" in hotKey) {
      setSelectedQuestionnaire({
        questionnaireName: hotKey.questionnaireName,
        displayTitle: hotKey.displayTitle,
      });
      setOpened(true);
    }
  };

  const handleClose = () => {
    setOpened(false);
    setSelectedQuestionnaire(null);
    setSelectedHotKey(null);
  };

  return (
    <>
      {!Object.values(HOT_KEY_BUTTONS)
        .filter(
          (button): button is HotKey & { questionnaireName: QuestionnaireType } =>
            "questionnaireName" in button && Boolean(button.questionnaireName),
        )
        .some(
          (button) =>
            opened && selectedQuestionnaire?.questionnaireName === button.questionnaireName,
        ) && (
        <Affix position={{ top: "50%", right: 0 }} style={{ transform: "translateY(-50%)" }}>
          <Stack
            p="xs"
            gap="xs"
            bg="white"
            style={{
              borderTopLeftRadius: 16,
              borderBottomLeftRadius: 16,
              boxShadow: "0 1px 3px 0px rgba(0, 0, 0, 0.05)",
              borderLeft: "1px solid var(--mantine-color-cream-4)",
              borderTop: "1px solid var(--mantine-color-cream-4)",
              borderBottom: "1px solid var(--mantine-color-cream-4)",
            }}
          >
            {HOT_KEY_BUTTONS.map((hotKey) => {
              const HotKeyIcon =
                HOT_KEY_ICONS[hotKey.displayTitle as keyof typeof HOT_KEY_ICONS] ||
                HOT_KEY_ICONS.Default;
              const isActiveReminder = hotKey.displayTitle === "Reminder" && isReminderActive;
              return (
                <HotKeyRow
                  key={hotKey.displayTitle}
                  shortLabel={
                    isActiveReminder ? REMINDER_LABELS.active.shortLabel : hotKey.shortLabel
                  }
                >
                  <HotKeyIcon
                    hotKey={hotKey}
                    clientId={clientId}
                    onRefresh={onRefresh}
                    encounterId={encounterId}
                    selectedHotKey={selectedHotKey}
                    isReminderActive={isReminderActive}
                    setSelectedHotKey={setSelectedHotKey}
                    onClick={() => handleHotKeyChange(hotKey)}
                  />
                </HotKeyRow>
              );
            })}
          </Stack>
        </Affix>
      )}

      {selectedQuestionnaire && (
        <EncounterLaborQuickButtonDrawer
          opened={opened}
          clientId={clientId}
          onClose={handleClose}
          onRefresh={onRefresh}
          encounterId={encounterId}
          questionnaireTitle={selectedQuestionnaire.displayTitle}
          questionnaireName={selectedQuestionnaire.questionnaireName}
        />
      )}
    </>
  );
}
